import React, { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { ReportTextCell, CrewDataTableFrame, CrewDataTablePrompt } from './reportShared';
import type { NationalityManningResponse, NationalityManningRow } from './types';

const NationalityManningPage: React.FC = () => {
  const { colors } = useTheme();
  const [vesselId, setVesselId] = useState('');
  const [appliedVesselId, setAppliedVesselId] = useState('');

  const listUrl = appliedVesselId
    ? `/crew/reports/nationality-manning?vesselId=${encodeURIComponent(appliedVesselId)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<NationalityManningResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const columns: Column<NationalityManningRow>[] = useMemo(
    () => [
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'count', header: 'Onboard', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'percent',
        header: '%',
        sortable: true,
        render: (v) => <ReportTextCell value={typeof v === 'number' ? `${v}%` : v} />,
      },
    ],
    [],
  );

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setAppliedVesselId(vesselId);
  }, [vesselId]);

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
          hint={{
          title: 'Nationalities',
          body: 'Onboard nationality mix for a vessel, with safe manning / safe equipment flags.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={appliedVesselId ? () => mutate() : undefined}
        context={
          data?.vessel
            ? `${data.vessel.vesselName}${data.vessel.flag ? ` · Flag ${data.vessel.flag}` : ''} · ${data.summary.crewCount} crew · Safe manning ${data.summary.safeManning != null ? data.summary.safeManning : '—'} · Safe equip ${data.summary.safeEquip ? 'Yes' : 'No'}`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!appliedVesselId ? (
          <CrewDataTablePrompt>Select a vessel, then search.
          </CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt><span className="text-red-600">Could not load manning mix.</span></CrewDataTablePrompt>
        ) : (
          <DataTable
            data={data?.rows ?? []}
            columns={columns}
            keyExtractor={(row) => String(row.nationalityId ?? row.nationality)}
            loading={isLoading}
            emptyMessage="No onboard crew"
          />
        )}
      </CrewDataTableFrame>
    </div>
  );
};

export default NationalityManningPage;
